import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { useCart } from '@/hooks/useCart';
import { useAuth } from '@/hooks/useAuth';
import { trackPurchase } from '@/lib/facebookPixel';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';
import { chargesVat, computeVat, VAT_RATE } from '@/lib/tax';
import { isDigitalProduct } from '@/lib/productType';
import { emitCoreActivity } from '@/lib/coreEventHooks';
import { dispatchCoreAction, newIdempotencyKey } from '@/lib/coreActionBridge';
import { notifyMerchantNewOrder } from '@/lib/orderNotify';
import { triggerPushForUser } from '@/lib/notifyPush';
import { notificationTargetUrl } from '@/lib/pushDevices';
import {
  loadPlatformPaymentConfig,
  loadMerchantCheckoutConfig,
  type PlatformPaymentConfig,
  type MerchantCheckoutConfig,
} from '@/lib/platformPayments';
import ShopPaymentModal from '@/components/feature/ShopPaymentModal';

export default function CommandePublic() {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { user } = useAuth();
  const { items, subtotal, currency, clearCart } = useCart();
  const { display, original } = useDisplayCurrency(currency);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [placed, setPlaced] = useState(false);
  const [orderId, setOrderId] = useState<number | null>(null);
  const [digitalDownloads, setDigitalDownloads] = useState<{ name: string; url: string | null }[]>([]);

  // Paiement en ligne de la boutique. Le paiement n'apparaît QUE si le
  // commerce est autorisé par le SuperAdmin (portée globale ou accès nominatif)
  // ET qu'il a choisi au moins un moyen activé. Sinon, le checkout reste
  // exactement comme avant : commande sans paiement en ligne.
  const [platform, setPlatform] = useState<PlatformPaymentConfig | null>(null);
  const [merchantPay, setMerchantPay] = useState<MerchantCheckoutConfig | null>(null);
  const [payMode, setPayMode] = useState<'offline' | 'online'>('offline');
  // Fenêtre de paiement (même modale que le tunnel d'abonnement).
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [paidInfo, setPaidInfo] = useState<{ amount: number; currency: string; reference: string } | null>(null);
  // Instantané de la commande : le panier est vidé après l'envoi, on garde
  // donc le total, la devise et les fichiers digitaux pour l'écran de fin.
  const [placedOrder, setPlacedOrder] = useState<{
    total: number;
    currency: string;
    digital: boolean;
    files: { name: string; url: string | null }[];
    productIds: number[];
  } | null>(null);

  useEffect(() => {
    let active = true;
    void loadPlatformPaymentConfig().then((cfg) => {
      if (active) setPlatform(cfg);
    });
    return () => { active = false; };
  }, []);

  // Autorisation + moyens choisis par CE commerce.
  useEffect(() => {
    if (!tenant?.id) { setMerchantPay(null); return; }
    let active = true;
    void loadMerchantCheckoutConfig(tenant.id).then((cfg) => {
      if (!active) return;
      setMerchantPay(cfg);
      if (cfg.onlineEnabled) setPayMode('online');
    });
    return () => { active = false; };
  }, [tenant?.id]);

  /** Paiement en ligne réellement disponible sur cette boutique. */
  const onlinePaymentAvailable = Boolean(merchantPay?.onlineEnabled);
  /** Prestataire de paiement qui encaisse réellement (mention légale). */
  const partnerLabel = merchantPay?.methods.find((m) => m.ready)?.label || 'FeexPay';

  // ── Retour depuis la page de paiement FeexPay (?order=<id>) ──
  // On interroge le statut réel de la transaction : le webhook confirme le
  // paiement côté serveur, la page se rafraîchit donc toute seule.
  const [searchParams] = useSearchParams();
  const returnOrderId = Number(searchParams.get('order')) || 0;
  const [returnInfo, setReturnInfo] = useState<{
    state: 'loading' | 'pending' | 'paid' | 'failed' | 'unknown';
    amount: number;
    currency: string;
    reference: string;
  } | null>(null);
  const [returnRefresh, setReturnRefresh] = useState(0);

  useEffect(() => {
    if (!returnOrderId) return;
    let active = true;
    let attempts = 0;
    let timer: number | undefined;

    const check = async () => {
      attempts += 1;
      try {
        const [orderRes, payRes] = await Promise.all([
          supabase
            .from('commande')
            .select('id, totalttc, monaie, statut_com_vendeur, sommepayee')
            .eq('id', returnOrderId)
            .maybeSingle(),
          supabase
            .from('zifek_payments')
            .select('id, status, amount, currency, reference')
            .eq('method', 'shop_feexpay')
            .like('payload', `%"order_id":${returnOrderId}%`)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle(),
        ]);
        if (!active) return;

        const order = orderRes.data as {
          totalttc?: string | number | null;
          monaie?: string | null;
          statut_com_vendeur?: string | null;
          sommepayee?: string | null;
        } | null;
        const payment = payRes.data as {
          status?: string | null;
          amount?: number | null;
          currency?: string | null;
          reference?: string | null;
        } | null;

        const payStatus = (payment?.status || '').toLowerCase();
        const orderStatus = (order?.statut_com_vendeur || '').toLowerCase();
        const alreadyPaid = Number(order?.sommepayee) > 0;

        let state: 'loading' | 'pending' | 'paid' | 'failed' | 'unknown' = 'pending';
        if (payStatus === 'paid' || alreadyPaid || ['confirmée', 'preparee', 'préparation', 'expédiée', 'livrée'].includes(orderStatus)) {
          state = 'paid';
        } else if (payStatus === 'failed' || payStatus === 'cancelled') {
          state = 'failed';
        } else if (!payment && !order) {
          state = 'unknown';
        }

        setReturnInfo({
          state,
          amount: Number(payment?.amount ?? order?.totalttc) || 0,
          currency: payment?.currency || order?.monaie || currency,
          reference: payment?.reference || '',
        });

        if (state === 'pending' && attempts < 25) {
          timer = window.setTimeout(check, 6000);
        }
      } catch {
        if (active) setReturnInfo({ state: 'unknown', amount: 0, currency, reference: '' });
      }
    };

    void check();
    return () => {
      active = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [returnOrderId, currency, returnRefresh]);
  // Clé d'idempotence mémorisée le temps d'un envoi (retry-safe, spec 47).
  const idemKeyRef = useRef<string>('');

  // Pré-remplissage depuis le compte connecté : nom, email, téléphone,
  // ville et adresse sont repris du profil pour éviter la double saisie.
  useEffect(() => {
    if (!user) return;
    const city = String(user.Ville || '').trim();
    const quartier = String(user.Quartier || '').trim();
    const adresse = String(user.adresse || '').trim();
    const addressParts = [adresse, quartier].filter(Boolean);
    setName((prev) => prev || user.name || '');
    setEmail((prev) => prev || user.email || '');
    setPhone((prev) => prev || user.telephone || '');
    setCity((prev) => prev || city);
    setAddress((prev) => prev || addressParts.join(', '));
  }, [user]);

  const totalCount = items.reduce((s, i) => s + i.quantity, 0);
  const digitalItems = items.filter((i) => isDigitalProduct(i.product));
  const allDigital = items.length > 0 && digitalItems.length === items.length;
  // TVA : uniquement si le marchand a renseigné son numéro d'inscription à la taxe.
  const vatEnabled = chargesVat(tenant?.tva);
  const vat = computeVat(subtotal, tenant?.tva);
  const grandTotal = subtotal + vat;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (items.length === 0) return;

    // Paiement en ligne : la session doit être ouverte. Le paiement est
    // rattaché au compte client, on ne l'accepte donc pas en visiteur.
    if (payMode === 'online' && onlinePaymentAvailable && !user) {
      setError('Connectez-vous à votre compte pour régler en ligne : votre commande et son paiement restent rattachés à votre espace client.');
      return;
    }

    const cleanName = name.trim();
    const cleanPhone = phone.trim();
    const cleanEmail = email.trim();
    if (!cleanName) {
      setError('Veuillez indiquer votre nom.');
      return;
    }
    if (allDigital) {
      if (!cleanEmail) {
        setError('Veuillez indiquer votre email : vos fichiers y seront rattachés.');
        return;
      }
    } else if (!cleanPhone && !cleanEmail) {
      // Le téléphone n'est plus obligatoire : un email suffit pour être
      // recontacté au sujet de la livraison.
      setError('Indiquez au moins un téléphone ou un email : le marchand doit pouvoir vous joindre pour la livraison.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const details = items
        .map((i) => `${i.product.name} ×${i.quantity}`)
        .join(', ');
      const clientId = cleanPhone || email.trim() || `invite_${Date.now()}`;
      // Identifiant lié au compte Zifek : sert au client pour retrouver ses achats.
      const accountUserId = user?.id ? String(user.id) : clientId;

      // ── Action Layer (46) : création de commande idempotente (47).
      // Un rejeu réseau / double envoi ne créera pas de doublon.
      if (!idemKeyRef.current) idemKeyRef.current = newIdempotencyKey('order');

      const actionResult = await dispatchCoreAction({
        commerceId: tenant?.id ?? null,
        actionKey: 'create_order',
        entityType: 'order',
        idempotencyKey: idemKeyRef.current,
        idempotencyScope: 'orders',
        source: 'boutique',
        actorName: cleanName,
        actorUserId: user?.id ?? null,
        parameters: { items: totalCount, total: grandTotal, currency, digital: allDigital },
        execute: async () => {
          // Création de commande ATOMIQUE côté base (spec 70) : une seule
          // transaction enveloppe la commande + son entête + ses lignes + la
          // notification marchand. Si une étape échoue, rien n'est créé —
          // aucun état partiellement créé.
          const orderPayload = {
            idvendeur: tenant?.id ?? 0,
            owner: tenant?.id ?? 0,
            titre: `Commande ${tenant?.nomcommerce || 'boutique'}`,
            typecommande: allDigital ? 'digital' : 'produit',
            serviceouproduit: 1,
            nomclient: cleanName,
            email: email.trim(),
            tel: cleanPhone,
            adresse: address.trim(),
            whatsapp: cleanPhone,
            details,
            user_id: accountUserId,
            monaie: currency,
            methodepayment: payMode === 'online' ? 'Zifek en ligne' : 'A convenir',
            totalht: String(subtotal),
            totalttc: String(grandTotal),
            quantite: totalCount,
            statut_com_vendeur: 'En attente',
            statut_com_client: 'En attente',
            ville: city.trim(),
            noteclient: notes.trim(),
            items_json: items.map((i) => ({
              id: i.product.id,
              name: i.product.name,
              quantity: i.quantity,
              product_type: i.product.product_type || 'physical',
              price: i.product.price,
            })),
            currency,
            subtotal_items: subtotal,
            tax_total: vat || null,
            customer_notes: notes.trim() || null,
            recipient: {
              name: cleanName,
              email: email.trim(),
              phone: cleanPhone,
              city: city.trim(),
              address: address.trim(),
            },
            customer_id: user?.id ? String(user.id) : null,
            items: items.map((i) => ({
              product_id: String(i.product.id),
              product_name: i.product.name,
              quantity: i.quantity,
              unit_price: i.product.original_price ?? i.product.price,
              final_price: i.product.price,
              subtotal: i.product.price * i.quantity,
            })),
            notify_user_id: tenant?.id ?? null,
            notify_sender_id: user?.id ?? 0,
            notify_message: `Nouvelle commande — ${totalCount} article${totalCount > 1 ? 's' : ''} — ${grandTotal.toLocaleString()} ${currency}`,
          };

          const { data: rpcData, error: rpcError } = await supabase.rpc('core_create_order_atomic', {
            p_org_id: null,
            p_tenant_id: tenant?.id ?? null,
            p_order: orderPayload,
          });

          if (rpcError) throw rpcError;
          const created = (rpcData || {}) as { commande_id?: number; header_id?: number; items?: number };
          const createdOrderId = created.commande_id ?? null;

          // Événement du socle (Event Bus + Timeline + invalidation du cache).
          await emitCoreActivity({
            commerceId: tenant?.id ?? null,
            eventKey: 'order.created',
            entityType: 'order',
            entityId: createdOrderId ?? 0,
            title: `Commande #${createdOrderId ?? ''} créée`,
            description: details,
            amount: grandTotal,
            currency,
            payload: { customer: cleanName, items: totalCount, type: allDigital ? 'digital' : 'physical' },
            actorName: cleanName,
            source: 'boutique',
          });

          return { entityId: createdOrderId, result: { order_id: createdOrderId, header_id: created.header_id ?? null, items: created.items ?? 0 } };
        },
      });

      if (!actionResult.ok) {
        throw new Error(actionResult.error || "Une erreur est survenue lors de l'envoi de votre commande.");
      }
      idemKeyRef.current = '';
      const createdId = actionResult.entityId ? Number(actionResult.entityId) : null;

      // E-mail + WhatsApp au marchand (best-effort, en plus du push interne).
      void notifyMerchantNewOrder({
        commerceId: tenant?.id ?? null,
        orderId: createdId,
        customerName: cleanName,
        items: items.map((i) => ({ name: i.product.name, quantity: i.quantity })),
        total: grandTotal,
        currency,
      });
      // 📲 Notification PWA (Web Push) au marchand, même app fermée.
      // Cliquer dessus ouvre directement la page des commandes.
      void triggerPushForUser(tenant?.id ?? null, {
        title: 'Nouvelle commande',
        body: `Commande — ${totalCount} article${totalCount > 1 ? 's' : ''} — ${grandTotal.toLocaleString()} ${currency}`,
        url: notificationTargetUrl('order', { publicationId: createdId }),
      });

      // Suivi Facebook Pixel : achat réalisé
      trackPurchase({
        value: grandTotal,
        currency,
        content_ids: items.map((i) => String(i.product.id)),
        num_items: totalCount,
      });

      setOrderId(createdId);

      // 🔒 Produits digitaux : les fichiers ne sont JAMAIS exposés avant le
      // paiement. En paiement en ligne, l'accès est débloqué par le serveur
      // dès la confirmation de l'opérateur.
      const requiresOnlinePayment = payMode === 'online' && onlinePaymentAvailable;
      const files = digitalItems.map((i) => ({ name: i.product.name, url: i.product.digital_file_url || null }));
      setPlacedOrder({
        total: grandTotal,
        currency,
        digital: allDigital,
        files,
        productIds: items.map((i) => Number(i.product.id)).filter((id) => Number.isFinite(id) && id > 0),
      });
      setDigitalDownloads(requiresOnlinePayment ? [] : files);

      // Le panier est vidé juste après : le total utilisé ensuite vient de
      // l'instantané `placedOrder`.
      setPlaced(true);
      clearCart();

      // Paiement en ligne : on n'envoie AUCUNE information de paiement ici.
      // La fenêtre FeexPay (pays, opérateur, numéro, montant) s'ouvre et
      // c'est elle qui collecte les détails. Rien n'est débité avant.
      if (requiresOnlinePayment && createdId) {
        setPayModalOpen(true);
      }
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Une erreur est survenue lors de l'envoi de votre commande."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Écran de confirmation
  if (placed) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-check-line text-3xl text-accent-600"></i>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
            Commande envoyée !
          </h1>
          <p className="text-foreground-500 mb-6">
            Merci pour votre commande{orderId ? ` (n° ${orderId})` : ''}.
            {placedOrder?.digital
              ? ' Vos fichiers sont disponibles ci-dessous sous forme de liens de téléchargement.'
              : ' Nous vous contacterons rapidement pour convenir de la livraison et du paiement.'}
          </p>

          {paidInfo ? (
            <div className="mb-6 text-left bg-accent-50 border border-accent-200/70 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-checkbox-circle-fill text-accent-600"></i>
                <span className="text-sm font-bold text-foreground-900">Paiement confirmé</span>
              </div>
              <p className="text-sm text-foreground-700 leading-relaxed">
                Votre paiement de{' '}
                <strong>{paidInfo.amount.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {paidInfo.currency}</strong>{' '}
                a été encaissé. La commande {orderId ? `n° ${orderId}` : ''} est confirmée et le marchand a été prévenu.
              </p>
              {paidInfo.reference && (
                <p className="mt-2 text-[11px] text-foreground-500 break-all">Référence : {paidInfo.reference}</p>
              )}
            </div>
          ) : onlinePaymentAvailable && (
            <div className="mb-6 text-left bg-primary-50 border border-primary-200/70 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-secure-payment-line text-primary-600"></i>
                <span className="text-sm font-bold text-foreground-900">Régler ma commande maintenant</span>
              </div>
              <p className="text-xs text-foreground-600 leading-relaxed">
                La fenêtre de paiement s’ouvre ici même : le montant est déjà rempli (modifiable), vous
                choisissez votre pays, votre opérateur et vous validez sur votre téléphone.
              </p>
              <p className="mt-2 flex items-center gap-1.5 text-[11px] text-foreground-400">
                <i className="ri-shield-check-line"></i>
                Paiement géré par notre partenaire {partnerLabel}
              </p>
              <button
                type="button"
                onClick={() => setPayModalOpen(true)}
                className="mt-3 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-bank-card-line"></i>
                Payer {(placedOrder?.total ?? 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {currency}
              </button>
            </div>
          )}

          {digitalDownloads.length === 0 && placedOrder?.digital && (
            <div className="mb-6 text-left bg-secondary-50 border border-secondary-100 rounded-xl p-5">
              <div className="flex items-start gap-2">
                <i className="ri-lock-line text-secondary-600 mt-0.5"></i>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">Vos fichiers seront débloqués après paiement</p>
                  <p className="text-xs text-foreground-600 mt-0.5 leading-relaxed">
                    Dès que l’opérateur confirme votre paiement, l’accès à vos produits digitaux est
                    ouvert automatiquement dans votre espace client (onglet Téléchargements).
                  </p>
                </div>
              </div>
            </div>
          )}

          {digitalDownloads.length > 0 && (
            <div className="mb-6 text-left bg-secondary-50 border border-secondary-100 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-download-cloud-2-line text-secondary-600"></i>
                <span className="text-sm font-bold text-foreground-900">Vos fichiers à télécharger</span>
              </div>
              <div className="space-y-2">
                {digitalDownloads.map((d, i) => (
                  <div key={i} className="flex items-center gap-3 bg-background-50 border border-background-200/70 rounded-lg p-3">
                    <i className="ri-file-line text-primary-500"></i>
                    <span className="text-sm text-foreground-800 truncate flex-1">{d.name}</span>
                    {d.url ? (
                      <a href={d.url} target="_blank" rel="noopener noreferrer" download
                        className="px-3 py-1.5 rounded-full text-xs font-semibold bg-secondary-500 text-background-50 hover:bg-secondary-600 transition-colors no-underline whitespace-nowrap">
                        <i className="ri-download-2-line mr-1"></i>Télécharger
                      </a>
                    ) : (
                      <span className="text-xs text-foreground-400 whitespace-nowrap">Lien à venir</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-foreground-500 mt-3">Conservez cette page ou vos identifiants : vos fichiers restent accessibles depuis votre commande.</p>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => navigate('/products')}
              className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Continuer mes achats
            </button>
            <Link
              to="/"
              className="px-6 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer no-underline whitespace-nowrap"
            >
              Retour à l&apos;accueil
            </Link>
          </div>
        </div>

        {payModalOpen && orderId && placedOrder && (
          <ShopPaymentModal
            orderId={orderId}
            total={placedOrder.total}
            currency={placedOrder.currency}
            buyerName={name}
            buyerEmail={email}
            buyerPhone={phone}
            merchantName={tenant?.nomcommerce || 'le marchand'}
            onClose={() => setPayModalOpen(false)}
            onPaid={(info) => {
              setPaidInfo(info);
              setPayModalOpen(false);
              setDigitalDownloads(placedOrder.files);
              // Accès digital accordé par le serveur au moment du paiement :
              // on relit les vrais fichiers du produit pour un téléchargement
              // immédiat, même si le panier ne les avait pas chargés.
              if (placedOrder.digital && placedOrder.productIds.length > 0) {
                void supabase
                  .from('product_items')
                  .select('id, name, digital_file_url')
                  .in('id', placedOrder.productIds)
                  .then(({ data }) => {
                    const rows = (data || []) as { name?: string | null; digital_file_url?: string | null }[];
                    if (rows.length === 0) return;
                    setDigitalDownloads(rows.map((p) => ({
                      name: String(p.name || 'Fichier'),
                      url: p.digital_file_url || null,
                    })));
                  });
              }
            }}
          />
        )}
      </div>
    );
  }

  // Retour depuis la page de paiement (FeexPay) : on affiche l'état réel
  // de la transaction, mis à jour automatiquement dès la confirmation.
  if (returnOrderId) {
    const state = returnInfo?.state || 'loading';
    const paid = state === 'paid';
    const failed = state === 'failed';
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${paid ? 'bg-accent-100' : failed ? 'bg-red-50' : 'bg-secondary-100'}`}>
            {paid ? (
              <i className="ri-check-line text-3xl text-accent-600"></i>
            ) : failed ? (
              <i className="ri-close-line text-3xl text-red-500"></i>
            ) : (
              <i className="ri-loader-4-line animate-spin text-2xl text-secondary-700"></i>
            )}
          </div>

          <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
            {paid ? 'Paiement confirmé' : failed ? 'Paiement refusé' : 'Vérification du paiement'}
          </h1>

          <p className="text-sm text-foreground-500 mb-5">
            {paid
              ? `Merci ! Votre commande n° ${returnOrderId} est payée et confirmée. Le marchand a été prévenu.`
              : failed
                ? `Le paiement de la commande n° ${returnOrderId} n'a pas abouti. Vous pouvez réessayer ou contacter le marchand.`
                : `Nous vérifions le paiement de la commande n° ${returnOrderId}. Cette page se met à jour automatiquement dès la confirmation par l'opérateur.`}
          </p>

          {returnInfo && returnInfo.amount > 0 && (
            <div className="px-4 py-3 bg-background-100/70 rounded-lg text-xs text-foreground-600 mb-6 text-left space-y-1">
              <p className="flex items-center justify-between gap-3">
                <span>Commande</span>
                <span className="font-medium text-foreground-800">#{returnOrderId}</span>
              </p>
              <p className="flex items-center justify-between gap-3">
                <span>Montant</span>
                <span className="font-medium text-foreground-800">
                  {returnInfo.amount.toLocaleString('fr-FR')} {returnInfo.currency}
                </span>
              </p>
              {returnInfo.reference && (
                <p className="flex items-center justify-between gap-3">
                  <span>Référence</span>
                  <span className="font-mono text-foreground-800 break-all text-right">{returnInfo.reference}</span>
                </p>
              )}
              <p className="flex items-center justify-between gap-3">
                <span>Statut</span>
                <span className={`font-semibold ${paid ? 'text-accent-700' : failed ? 'text-red-600' : 'text-secondary-700'}`}>
                  {paid ? 'Payé' : failed ? 'Échoué' : 'En attente de confirmation'}
                </span>
              </p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => { setReturnInfo(null); setReturnRefresh((n) => n + 1); }}
              className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line mr-1.5"></i>
              Actualiser le statut
            </button>
            <Link
              to="/"
              className="px-6 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer no-underline whitespace-nowrap"
            >
              Retour à l&apos;accueil
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Panier vide
  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-shopping-cart-line text-2xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-bold font-heading text-foreground-950 mb-2">Votre panier est vide</h1>
          <p className="text-foreground-500 mb-6">Ajoutez des produits avant de passer commande.</p>
          <Link
            to="/products"
            className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer no-underline whitespace-nowrap"
          >
            Voir les produits
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <section className="relative py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background-100/50 to-transparent"></div>
        <div className="relative w-full px-4 md:px-6 max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-2">
            Finaliser ma commande
          </h1>
          <p className="text-sm text-foreground-500">Renseignez vos coordonnées, nous vous recontactons</p>
        </div>
      </section>

      <section className="pb-12 md:pb-16">
        <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
          {!user && (
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <span className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-user-shared-line"></i>
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground-900">Connectez-vous pour payer en ligne</p>
                <p className="text-[11px] text-foreground-600 mt-0.5">
                  Vos coordonnées sont reprises automatiquement et votre paiement reste lié à votre compte.
                  Vous pouvez aussi commander sans compte et régler à la livraison.
                </p>
              </div>
              <Link
                to="/connexion?redirect=/checkout"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-foreground-950 text-background-50 text-xs font-semibold no-underline whitespace-nowrap hover:bg-foreground-800 transition-colors self-start sm:self-auto"
              >
                <i className="ri-login-circle-line"></i>
                Se connecter
              </Link>
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Formulaire */}
            <form onSubmit={handleSubmit} className="lg:col-span-3 bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 space-y-4">
              <div>
                <label htmlFor="cmd-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Nom complet <span className="text-red-500">*</span>
                </label>
                <input
                  id="cmd-name"
                  name="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Votre nom"
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                  required
                />
              </div>

              <div>
                <label htmlFor="cmd-phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Téléphone <span className="text-foreground-400 font-normal">(facultatif)</span>
                </label>
                <input
                  id="cmd-phone"
                  name="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Ex : 06 12 34 56 78"
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                />
                <p className="text-[11px] text-foreground-400 mt-1">
                  Un email suffit si vous préférez ne pas laisser de numéro.
                </p>
              </div>

              <div>
                <label htmlFor="cmd-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Email
                </label>
                <input
                  id="cmd-email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Votre email"
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                />
              </div>

              {!allDigital && (
              <div className="space-y-4">
                <div>
                  <label htmlFor="cmd-city" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Ville
                  </label>
                  <input
                    id="cmd-city"
                    name="city"
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Votre ville"
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                  />
                </div>
                <div>
                  <label htmlFor="cmd-address" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Adresse de livraison
                  </label>
                  <input
                    id="cmd-address"
                    name="address"
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Rue, numéro, quartier, point de repère…"
                    className="w-full px-4 py-3.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                  />
                  <p className="text-[11px] text-foreground-400 mt-1">
                    Plus vous êtes précis, plus la livraison sera rapide.
                  </p>
                </div>
              </div>
              )}

              {allDigital && (
                <div className="px-4 py-3 bg-secondary-50 text-secondary-700 rounded-lg text-xs flex items-center gap-2">
                  <i className="ri-download-cloud-2-line"></i>
                  Commande 100% digitale : aucune adresse de livraison n&apos;est nécessaire.
                </div>
              )}

              <div>
                <label htmlFor="cmd-notes" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Remarques
                </label>
                <textarea
                  id="cmd-notes"
                  name="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Précisions utiles (point de livraison, créneau, etc.)"
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 resize-none"
                ></textarea>
              </div>

              {onlinePaymentAvailable && (
                <div>
                  <span className="block text-sm font-medium text-foreground-700 mb-1.5">Mode de paiement</span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setPayMode('online')}
                      className={`flex items-start gap-2.5 rounded-lg border p-3 text-left transition-colors cursor-pointer ${payMode === 'online' ? 'border-primary-300 bg-primary-50/60' : 'border-background-200/70 hover:border-background-300/60'}`}
                    >
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${payMode === 'online' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-500'}`}>
                        <i className="ri-bank-card-line"></i>
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-foreground-900">Payer maintenant</span>
                        <span className="block text-[11px] text-foreground-500">Mobile Money ou carte, en ligne</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPayMode('offline')}
                      className={`flex items-start gap-2.5 rounded-lg border p-3 text-left transition-colors cursor-pointer ${payMode === 'offline' ? 'border-primary-300 bg-primary-50/60' : 'border-background-200/70 hover:border-background-300/60'}`}
                    >
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${payMode === 'offline' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-500'}`}>
                        <i className="ri-hand-coin-line"></i>
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-foreground-900">À la livraison</span>
                        <span className="block text-[11px] text-foreground-500">Paiement à convenir avec le marchand</span>
                      </span>
                    </button>
                  </div>
                  {payMode === 'online' && (
                    <div className="mt-2.5 rounded-lg border border-background-200/70 bg-background-100/50 p-3 space-y-2.5">
                      <div className="flex items-start gap-2">
                        <i className="ri-smartphone-line text-primary-500 mt-0.5"></i>
                        <p className="text-[11px] text-foreground-600 leading-relaxed">
                          Après validation, la fenêtre de paiement{' '}
                          <strong>{merchantPay?.methods.find((m) => m.ready)?.label || 'FeexPay'}</strong>{' '}
                          s’ouvre directement ici : vous y choisissez votre pays, votre opérateur et le
                          montant. Rien n’est débité avant votre confirmation sur votre téléphone.
                        </p>
                      </div>

                      {!user && (
                        <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2">
                          <i className="ri-lock-line text-amber-700 mt-0.5"></i>
                          <div className="min-w-0">
                            <p className="text-[11px] font-semibold text-foreground-900">
                              Connexion requise pour payer en ligne
                            </p>
                            <p className="text-[11px] text-foreground-600">
                              Le paiement est rattaché à votre compte client.
                            </p>
                            <Link
                              to="/connexion?redirect=/checkout"
                              className="mt-1.5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-foreground-950 text-background-50 text-[11px] font-semibold no-underline whitespace-nowrap hover:bg-foreground-800 transition-colors"
                            >
                              <i className="ri-user-shared-line"></i>
                              Se connecter
                            </Link>
                          </div>
                        </div>
                      )}

                      <p className="text-[11px] text-foreground-500 leading-relaxed">
                        Des frais de {merchantPay?.commission ?? platform?.commission ?? 6} % (FeexPay et
                        Mobile Money inclus) s’appliquent à la transaction.
                      </p>

                      <div className="flex items-start gap-2 rounded-md bg-background-100 px-3 py-2">
                        <i className="ri-shield-check-line text-accent-600 mt-0.5"></i>
                        <p className="text-[11px] text-foreground-600 leading-relaxed">
                          Paiement géré par notre partenaire <strong>{partnerLabel}</strong>. Vos informations de
                          paiement sont saisies chez le prestataire sécurisé, jamais sur la boutique.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className="px-4 py-3 bg-red-50 text-red-600 rounded-lg text-sm flex items-center gap-2">
                  <i className="ri-error-warning-line"></i>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-bold hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <i className={payMode === 'online' && onlinePaymentAvailable ? 'ri-secure-payment-line' : 'ri-check-line'}></i>
                    {payMode === 'online' && onlinePaymentAvailable ? 'Confirmer et payer' : 'Confirmer ma commande'}
                  </>
                )}
              </button>

              {onlinePaymentAvailable && payMode === 'online' && (
                <p className="flex items-center justify-center gap-1.5 text-[11px] text-foreground-400">
                  <i className="ri-lock-2-line"></i>
                  Paiement sécurisé géré par notre partenaire {partnerLabel}
                </p>
              )}
            </form>

            {/* Résumé */}
            <div className="lg:col-span-2">
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 sticky top-20">
                <h2 className="text-sm font-bold font-heading text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-receipt-line text-primary-500"></i>
                  Récapitulatif
                </h2>
                <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
                  {items.map((item) => (
                    <div key={item.product.id} className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-md bg-background-100 overflow-hidden flex-shrink-0">
                        {item.product.media ? (
                          <img
                            src={item.product.media}
                            alt={item.product.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <i className="ri-shopping-bag-line text-foreground-300"></i>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground-900 truncate">{item.product.name}</p>
                        <p className="text-xs text-foreground-400">×{item.quantity}</p>
                      </div>
                      <span className="text-xs font-semibold text-foreground-800 whitespace-nowrap">
                        {display(item.product.price * item.quantity, currency)}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-background-200/70 pt-3 space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-foreground-500">Sous-total</span>
                    <span className="text-foreground-800 tabular-nums">{display(subtotal, currency)}</span>
                  </div>
                  {vatEnabled && (
                    <div className="flex justify-between">
                      <span className="text-foreground-500">TVA ({(VAT_RATE * 100).toFixed(0)}%)</span>
                      <span className="text-foreground-800 tabular-nums">{display(vat, currency)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold pt-2 border-t border-background-200/70">
                    <span className="text-foreground-950">Total</span>
                    <span className="text-primary-600 tabular-nums">{display(grandTotal, currency)}</span>
                  </div>
                  {original(grandTotal, currency) && (
                    <div className="flex justify-between text-xs text-foreground-400 pt-1">
                      <span>Montant réel</span>
                      <span className="tabular-nums">{original(grandTotal, currency)}</span>
                    </div>
                  )}
                </div>

                <p className="text-xs text-foreground-400 mt-3 leading-relaxed">
                  {onlinePaymentAvailable && payMode === 'online'
                    ? `Paiement géré par notre partenaire ${partnerLabel} (frais de ${merchantPay?.commission ?? platform?.commission ?? 6} % inclus). La fenêtre de paiement s’ouvre après validation, la commande n’est payée qu’après confirmation de l’opérateur.`
                    : onlinePaymentAvailable
                      ? 'Vous choisissez « À la livraison » : aucun débit maintenant. Vous pourrez régler en ligne à tout moment depuis la confirmation de commande.'
                      : 'Aucun paiement en ligne n’est demandé. Le paiement se fera à la livraison ou selon vos convenances.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}